import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, isUnauthorized, TAB_FLAG } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { StorageService } from 'app/core/storage/storage.service';

/** Bez „Zapamiętaj mnie”: 30 minut bez aktywności kończy sesję (Issue 42). */
export const IDLE_LIMIT_MS = 30 * 60_000;
/** Odnowienie tokenu dostępu z wyprzedzeniem, gdy karta jest na wierzchu. */
const REFRESH_BEFORE_S = 120;
const TICK_MS = 30_000;
const ACTIVITY_WRITE_MS = 15_000;
const PING_TIMEOUT_MS = 300;
const CHANNEL = 'vz-session';
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Sesja w tle (Issue 42): odnawia token 2 min przed wygaśnięciem, gdy karta jest na wierzchu; bez „Zapamiętaj mnie”
 * kończy sesję po 30 min bez aktywności (aktywność wspólna dla kart) i po zamknięciu wszystkich kart panelu
 * (nowa karta pyta pozostałe, czy żyją). Wylogowanie w jednej karcie przenosi pozostałe na logowanie.
 */
@Injectable({ providedIn: 'root' })
export class SessionKeeper {
    private readonly auth = inject(AuthService);
    private readonly storage = inject(StorageService);
    private readonly router = inject(Router);
    private readonly zone = inject(NgZone);
    private readonly document = inject(DOCUMENT);

    private channel: BroadcastChannel | null = null;
    private lastWrite = 0;

    /** Przed pierwszą nawigacją: sesja bez „Zapamiętaj mnie” nie przeżywa zamknięcia wszystkich kart panelu. */
    async init(): Promise<void> {
        this.channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : null;
        this.channel?.addEventListener('message', (event) => {
            if (event.data === 'ping' && this.auth.hasSession() && this.tabFlag()) {
                this.channel?.postMessage('pong');
            }
        });

        if (this.auth.hasSession() && !this.storage.remember) {
            if (!this.tabFlag() && !(await this.otherTabAlive())) {
                this.auth.discard();
            } else if (this.idleFor() >= IDLE_LIMIT_MS) {
                // Strażnik trasy przeniesie na logowanie z powrotem na tę stronę
                this.auth.markExpired('idle');
                this.auth.discard();
            }
        }

        if (this.auth.hasSession()) {
            this.setTabFlag();
        }

        this.zone.runOutsideAngular(() => this.watch());
    }

    private watch(): void {
        const onActivity = () => this.markActive();

        ACTIVITY_EVENTS.forEach((type) => this.document.addEventListener(type, onActivity, { passive: true, capture: true }));
        this.document.addEventListener('visibilitychange', () => {
            if (this.document.visibilityState === 'visible') {
                this.tick();
            }
        });
        setInterval(() => this.tick(), TICK_MS);

        // Wylogowanie albo koniec sesji w innej karcie
        this.document.defaultView?.addEventListener('storage', (event) => {
            if ((event.key === 'refreshToken' || event.key === null) && !this.storage.refreshToken && this.auth.onProtectedPage()) {
                this.zone.run(() => this.router.navigateByUrl('/logowanie'));
            }
        });
    }

    private tick(): void {
        if (!this.auth.hasSession() || !this.auth.onProtectedPage()) {
            return;
        }

        if (!this.storage.remember && this.idleFor() >= IDLE_LIMIT_MS) {
            this.zone.run(() => this.auth.expire('idle'));

            return;
        }

        if (this.document.visibilityState === 'visible' && AuthUtils.isTokenExpired(this.storage.accessToken, REFRESH_BEFORE_S)) {
            this.auth.refresh().subscribe({
                error: (error) => {
                    if (isUnauthorized(error)) {
                        this.zone.run(() => this.auth.expire('expired'));
                    }
                },
            });
        }
    }

    private markActive(): void {
        const now = Date.now();

        if (now - this.lastWrite >= ACTIVITY_WRITE_MS && this.auth.hasSession()) {
            this.lastWrite = now;
            this.storage.lastActivity = now;
        }
    }

    private idleFor(): number {
        const last = this.storage.lastActivity;

        return last ? Date.now() - last : 0;
    }

    private otherTabAlive(): Promise<boolean> {
        const channel = this.channel;

        if (!channel) {
            // Bez BroadcastChannel nie sprawdzimy innych kart — zostaje limit bezczynności
            return Promise.resolve(true);
        }

        return new Promise((resolve) => {
            const onMessage = (event: MessageEvent) => {
                if (event.data === 'pong') {
                    finish(true);
                }
            };
            const finish = (alive: boolean) => {
                clearTimeout(timer);
                channel.removeEventListener('message', onMessage);
                resolve(alive);
            };
            const timer = setTimeout(() => finish(false), PING_TIMEOUT_MS);

            channel.addEventListener('message', onMessage);
            channel.postMessage('ping');
        });
    }

    private tabFlag(): boolean {
        try {
            return sessionStorage.getItem(TAB_FLAG) === '1';
        } catch {
            return false;
        }
    }

    private setTabFlag(): void {
        try {
            sessionStorage.setItem(TAB_FLAG, '1');
        } catch {
            // Bez sessionStorage karta po przeładowaniu zapyta pozostałe karty jeszcze raz
        }
    }
}
